// frontend/src/components/LoginModal.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { getAuthMethodByEmailDomain, isValidEmail } from "../utils/emailDomainRouter";
import { 
  signIn as amplifySignIn, 
  signUp as amplifySignUp, 
  confirmSignUp, 
  resendSignUpCode,
  resetPassword,         // REQUIRED: Forces 6-digit code for old users
  confirmResetPassword   // REQUIRED: Verifies 6-digit code for old users
} from "aws-amplify/auth";
import snapGradLogo from "../assets/SnapGrad-By-Snaphomz_01.png";
import { pushRedditConversionEvent } from "../utils/redditTracking";

const OTP_LEN = 6; // STRICTLY 6 DIGITS FOR EVERYONE NOW

// Helper to derive a display name from email
const deriveNameFromEmail = (email) => {
  const local = (email || "").split("@")[0] || "";
  const first = local.split(/[^a-zA-Z]+/).filter(Boolean)[0] || "";
  return first ? first[0].toUpperCase() + first.slice(1).toLowerCase() : "User";
};

export default function LoginModal({ isOpen, onClose }) {
  const { signIn: hostedSignIn, refreshSession } = useAuth();

  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);

  // Steps: 'EMAIL' | 'OTP_SIGNUP' (New User) | 'OTP_RESET' (Existing User)
  const [step, setStep] = useState("EMAIL"); 
  
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LEN).fill(""));
  const [sendLoading, setSendLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  
  // We generate a complex random password for new users so they never have to manage it.
  // We also use this to reset existing users' passwords behind the scenes.
  const tempPassword = useMemo(() => {
    const letters = Math.random().toString(36).replace(/[^a-z]+/g, "").slice(-7);
    const digit = Math.floor(Math.random() * 10);
    return `SnapGrad${letters}${digit}!@#`;
  }, []);

  const otpRefs = useRef([]);
  const autoResendTriggeredRef = useRef(false);

  const normalizedEmail = useMemo(() => (email || "").trim().toLowerCase(), [email]);
  const emailOk = useMemo(() => isValidEmail(normalizedEmail), [normalizedEmail]);

  const authMethod = useMemo(() => {
    if (!emailOk) return null;
    return getAuthMethodByEmailDomain(normalizedEmail); 
  }, [emailOk, normalizedEmail]);

  const otpValue = useMemo(() => otpDigits.join(""), [otpDigits]);
  const otpValueTrimmed = useMemo(() => otpValue.replace(/\D/g, ""), [otpValue]);
  const otpComplete = useMemo(() => otpValueTrimmed.length === OTP_LEN, [otpValueTrimmed]);

  const focusFirstOtp = () => setTimeout(() => otpRefs.current?.[0]?.focus?.(), 50);

  // ------------------------------------------------------------------
  // CORE LOGIC: The Unified 6-Digit Router
  // ------------------------------------------------------------------
  const handleContinue = async () => {
    setTouched(true);
    setError("");
    if (!emailOk) return;

    if (authMethod === "GOOGLE") {
      hostedSignIn("Google", normalizedEmail);
      return;
    }

    pushRedditConversionEvent({
      event: "sign_up",
      userData: normalizedEmail ? { email_address: normalizedEmail } : undefined,
      eventData: {
        action: "signup",
        source: "email_modal",
      },
    });

    setSendLoading(true);

    try {
      // STRATEGY: Try to Register (New User) first.
      // This sends a standard 6-digit Verification Code.
      await amplifySignUp({
        username: normalizedEmail,
        password: tempPassword,
        options: {
          userAttributes: {
            email: normalizedEmail,
            name: deriveNameFromEmail(normalizedEmail)
          },
          autoSignIn: false 
        }
      });

      // If successful, user is NEW
      setStep("OTP_SIGNUP");
      setOtpDigits(Array(OTP_LEN).fill(""));
      focusFirstOtp();

    } catch (err) {
      // If Register fails because "User already exists"...
      if (err.name === "UsernameExistsException") {
        // ...We switch to the RESET PASSWORD flow.
        // This forces Cognito to send its standard 6-digit code, bypassing the 8-digit legacy flow.
        await initiateResetFlow();
      } else {
        console.error("SignUp Error:", err);
        setError(err.message || "Failed to start login.");
      }
    } finally {
      setSendLoading(false);
    }
  };

  const initiateResetFlow = async () => {
    try {
      // This triggers the "Forgot Password" email which is ALWAYS 6 digits.
      await resetPassword({ username: normalizedEmail });
      
      setStep("OTP_RESET");
      setOtpDigits(Array(OTP_LEN).fill(""));
      focusFirstOtp();
    } catch (e) {
      console.error("Reset Flow Error:", e);
      
      // Edge case: If user exists but was never confirmed, 'resetPassword' might fail.
      // In that case, we resend the signup code instead.
      if (e.name === "LimitExceededException") {
         setError("Too many attempts. Please wait a moment.");
      } else {
         try {
            await resendSignUpCode({ username: normalizedEmail });
            setStep("OTP_SIGNUP");
            setOtpDigits(Array(OTP_LEN).fill(""));
            focusFirstOtp();
            return;
         } catch(inner) {
            setError(e.message || "Failed to send code.");
         }
      }
    }
  };

  // ------------------------------------------------------------------
  // VERIFICATION LOGIC
  // ------------------------------------------------------------------
  const verifyOtp = async () => {
    setError("");
    setVerifying(true);
    const code = otpValueTrimmed;

    try {
      if (step === "OTP_SIGNUP") {
        // CASE A: New User (Confirming Registration)
        await confirmSignUp({
          username: normalizedEmail,
          confirmationCode: code
        });
        
        // Auto-login with the temp password we generated
        await amplifySignIn({
            username: normalizedEmail,
            password: tempPassword 
        });

      } else {
        // CASE B: Existing User (Confirming Reset)
        // 1. Reset password to the new tempPassword using the 6-digit code
        await confirmResetPassword({
            username: normalizedEmail,
            confirmationCode: code,
            newPassword: tempPassword
        });
        
        // 2. Auto-login with the new tempPassword
        await amplifySignIn({
            username: normalizedEmail,
            password: tempPassword
        });
      }

      // Success! Refresh session and close modal.
      await handleLoginSuccess();

    } catch (e) {
      console.error("Verification Error:", e);
      setError(e.message || "Invalid code. Please try again.");
      setVerifying(false);
    }
  };

  const handleLoginSuccess = async () => {
    try {
        await refreshSession();
        
        const userObj = {
            email: normalizedEmail,
            name: deriveNameFromEmail(normalizedEmail),
            updatedAt: Date.now(),
        };
        localStorage.setItem("snapgrad_user", JSON.stringify(userObj));
        localStorage.setItem("userData", JSON.stringify(userObj));
        
        onClose?.();
    } catch(e) {
        console.error("Session refresh failed", e);
    } finally {
        setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    if (!emailOk) return;
    try {
        if (step === "OTP_SIGNUP") {
            await resendSignUpCode({ username: normalizedEmail });
        } else {
            // For existing users, resend means trigger reset again
            await resetPassword({ username: normalizedEmail });
        }
        alert("Code resent!");
    } catch (e) {
        setError(e.message || "Failed to resend code");
    }
  };

  // ------------------------------------------------------------------
  // UI EVENT HANDLERS
  // ------------------------------------------------------------------
  const handleGoogle = () => {
    const hint = emailOk ? normalizedEmail : "";
    hostedSignIn("Google", hint);
  };

  const onOtpChange = (idx, val) => {
    setError("");
    const digit = (val || "").replace(/\D/g, "").slice(-1);

    setOtpDigits((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });

    if (digit && idx < OTP_LEN - 1) {
      otpRefs.current?.[idx + 1]?.focus?.();
    }
  };

  const onOtpKeyDown = (idx, e) => {
    if (e.key === "Enter" && otpComplete && !verifying) {
      e.preventDefault();
      verifyOtp();
    }
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      otpRefs.current?.[idx - 1]?.focus?.();
    }
  };

  const onOtpPaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
    if (!text) return;

    e.preventDefault();
    const digits = text.slice(0, OTP_LEN).split("");
    setOtpDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < OTP_LEN; i++) next[i] = digits[i] || "";
      return next;
    });

    const lastFilled = Math.min(digits.length, OTP_LEN) - 1;
    setTimeout(() => otpRefs.current?.[Math.max(lastFilled, 0)]?.focus?.(), 0);
  };

  const handleReEnterEmail = () => {
    setError("");
    setStep("EMAIL");
    setOtpDigits(Array(OTP_LEN).fill(""));
  };

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setEmail("");
      setTouched(false);
      setStep("EMAIL");
      setOtpDigits(Array(OTP_LEN).fill(""));
      setSendLoading(false);
      setVerifying(false);
      setError("");
      autoResendTriggeredRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative max-w-lg w-full bg-white p-8 rounded-2xl shadow-xl space-y-6 text-center mx-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none"
          aria-label="Close"
        >
          ×
        </button>

        {step === "EMAIL" ? (
          <>
            <div className="space-y-2">
              <img src={snapGradLogo} alt="SnapGrad" className="mx-auto h-10 w-auto" />
              <h2 className="text-3xl font-bold text-gray-900">Log in or Sign up</h2>
              <p className="text-gray-500">
                To see more schools, please create an account or log back in.
              </p>
            </div>
            <div className="space-y-2 text-left">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                type="email"
                placeholder="Provide your email address"
                className={`w-full rounded-lg border px-4 py-3 outline-none transition ${
                  touched && !emailOk ? "border-red-400 focus:ring-2 focus:ring-red-200" : "border-gray-200 focus:ring-[#F36F21]/20"
                }`}
              />
              {touched && !emailOk && <p className="text-red-500 text-sm">Email is required</p>}
              {error && <p className="text-red-500 text-sm">{error}</p>}
            </div>
            <button
              onClick={handleContinue}
              disabled={sendLoading}
              className={`w-full bg-[#F36F21] text-white font-semibold py-3 rounded-lg transition duration-200 ${
                sendLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#d85f1c]"
              }`}
            >
              {sendLoading ? "Sending Code..." : "Continue"}
            </button>
            <div className="flex items-center gap-4">
              <div className="h-px bg-gray-200 flex-1" />
              <span className="text-gray-400 text-sm">OR</span>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <button onClick={handleGoogle} className="w-full border border-gray-200 bg-white text-gray-800 font-semibold py-3 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2">
               {/* Keep your Google SVG here */}
               <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.655 32.659 29.182 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.957 3.043l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" /><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.957 3.043l5.657-5.657C34.046 6.053 29.268 4 24 4c-7.682 0-14.344 4.293-17.694 10.691z" /><path fill="#4CAF50" d="M24 44c5.09 0 9.786-1.947 13.314-5.12l-6.149-5.209C29.107 35.091 26.694 36 24 36c-5.159 0-9.62-3.317-11.29-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" /><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.805 2.159-2.237 3.973-4.138 5.209l.003-.002 6.149 5.209C36.905 39.001 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" /></svg>
               <span>Continue with Google</span>
            </button>
            <p className="text-xs text-gray-500">
              By signing up, I agree to SnapCollege{" "}
              <a
                href="https://snaphomz.com/terms-and-conditions"
                className="text-[#F36F21] font-medium"
                target="_blank"
                rel="noreferrer"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href="https://snaphomz.com/privacy-policy"
                className="text-[#F36F21] font-medium"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
              .
            </p>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <img src={snapGradLogo} alt="SnapGrad" className="mx-auto h-10 w-auto" />
              <h2 className="text-3xl font-bold text-gray-900">Confirm your email</h2>
              <p className="text-gray-500">We sent a code to <span className="font-semibold text-gray-900">{normalizedEmail}</span></p>
            </div>

            {/* OTP INPUTS - Strictly 6 Digits */}
            <div className="flex items-center justify-center gap-3" onPaste={onOtpPaste}>
              {otpDigits.map((d, idx) => (
                <input
                  key={idx}
                  ref={(el) => (otpRefs.current[idx] = el)}
                  value={d}
                  onChange={(e) => onOtpChange(idx, e.target.value)}
                  onKeyDown={(e) => onOtpKeyDown(idx, e)}
                  inputMode="numeric"
                  maxLength={1}
                  className="w-12 h-12 text-center text-xl border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-[#F36F21]/20"
                />
              ))}
            </div>

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              onClick={verifyOtp}
              disabled={!otpComplete || verifying}
              className={`w-full bg-[#F36F21] text-white font-semibold py-3 rounded-lg transition duration-200 ${
                !otpComplete || verifying ? "opacity-60 cursor-not-allowed" : "hover:bg-[#d85f1c]"
              }`}
            >
              {verifying ? "Verifying..." : "Verify"}
            </button>

            <div className="text-sm text-gray-600">
              Didn’t get a code? <button onClick={handleReEnterEmail} className="text-[#F36F21] font-medium hover:underline">Re-enter email</button>
              <span className="mx-2 text-gray-300">|</span>
              <button onClick={handleResendCode} className="text-[#F36F21] font-medium hover:underline">Resend Code</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
